import { _ as _assign } from '../tslib.es6-c6da0ae6.js';
import { useEffect, useCallback, useRef } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { authStates, authStateKey } from '../models/user/index.js';
import { u as userLogout, r as refreshToken, g as getUser } from '../requestFactory-4dc9ec32.js';
import { u as useAuthContext, a as authStatus } from '../authContext-a01efdfb.js';
import { deleteCookie } from '../cookie/index.js';
import { a as authCookies } from '../constants-a3addbf5.js';
import { setStorage, getStorage } from '../services/browserStorage/index.js';
import errorLogger, { BUGSNAG_STATUSES } from '../services/errorLogger/index.js';
import usePageVisibility from '../usePageVisibility/index.js';
import useIsomorphicLayoutEffect from '../useIsomorphicLayoutEffect/index.js';

var isAbortOrNavigationError = function isAbortOrNavigationError(err) {
  var _a, _b;
  var error = err;
  return (error === null || error === void 0 ? void 0 : error.name) === 'AbortError' || (error === null || error === void 0 ? void 0 : error.message) === 'Failed to fetch' || ((_b = (_a = error === null || error === void 0 ? void 0 : error.message) === null || _a === void 0 ? void 0 : _a.includes) === null || _b === void 0 ? void 0 : _b.call(_a, 'NetworkError'));
};

var refreshTokenInsurance = 60;
var refreshTokenTimerId = null;
function useBearerAuthentication(disableRefreshCycle) {
  var _a = useAuthContext(),
    onTokenChange = _a.onTokenChange,
    onStatusChange = _a.onStatusChange,
    onUserChange = _a.onUserChange,
    user = _a.user,
    tokenData = _a.tokenData,
    tokenDataRef = _a.tokenDataRef;
  useEffect(function () {
    tokenDataRef && tokenDataRef.current !== tokenData && (tokenDataRef.current = tokenData || null);
  }, [tokenData, tokenDataRef]);
  var tokenChange = useCallback(function (token) {
    tokenDataRef && (tokenDataRef.current = token || null);
    onTokenChange === null || onTokenChange === void 0 ? void 0 : onTokenChange(token);
  }, [onTokenChange, tokenDataRef]);
  var isTokenFetchedRef = useRef(false);
  var deleteAuthCookies = useCallback(function () {
    authCookies.forEach(function (cookieKey) {
      deleteCookie(cookieKey);
    });
  }, []);
  var makeLogout = useCallback(function () {
    var _a;
    var currentToken = (_a = tokenDataRef === null || tokenDataRef === void 0 ? void 0 : tokenDataRef.current) === null || _a === void 0 ? void 0 : _a.access_token;
    unstable_batchedUpdates(function () {
      var _a;
      setStorage(authStateKey, authStates.unauthorized);
      deleteAuthCookies();
      refreshTokenTimerId && clearTimeout(refreshTokenTimerId);
      refreshTokenTimerId = null;
      tokenChange(undefined);
      onUserChange === null || onUserChange === void 0 ? void 0 : onUserChange(null);
      onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(authStatus.failed);
      (_a = window === null || window === void 0 ? void 0 : window.pulse) === null || _a === void 0 ? void 0 : _a.call(window, 'state', {
        user_id: null
      });
    });
    if (currentToken) {
      userLogout(currentToken)["catch"](function (error) {
        errorLogger.trigger(error, BUGSNAG_STATUSES.ERROR);
      });
    }
  }, [deleteAuthCookies, onStatusChange, onUserChange, tokenChange, tokenDataRef]);
  var startTokenLifecycle = useCallback(function (props) {
    if (!(props === null || props === void 0 ? void 0 : props.access_token)) {
      makeLogout();
      return;
    }
    tokenChange(props);
    setStorage(authStateKey, authStates.authorized);
    var refreshTokenExpiresIn = (props.expires_in - refreshTokenInsurance) * 1000;
    onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(function (prev) {
      if (user) {
        return authStatus.resolved;
      }
      if (prev !== authStatus.resolved) {
        return authStatus.done;
      }
      return prev;
    });
    if (disableRefreshCycle) {
      return;
    }
    refreshTokenTimerId && clearTimeout(refreshTokenTimerId);
    refreshTokenTimerId = setTimeout(function () {
      refreshToken().then(function (tokenRes) {
        startTokenLifecycle(tokenRes.response);
      })["catch"](function (err) {
        if (!isAbortOrNavigationError(err)) {
          makeLogout();
        }
      });
    }, refreshTokenExpiresIn);
  }, [disableRefreshCycle, makeLogout, onStatusChange, tokenChange, user]);
  var extractUserInfo = function extractUserInfo(data) {
    var registered = data.registered,
      subscription = data.subscription;
    var is_new = !!registered;
    var subscribed = subscription && subscription.granted;
    var isTrialUsed = subscription ? !!subscription.is_trial_used : false;
    return _assign(_assign({}, data), {
      isTrialUsed: isTrialUsed,
      subscribed: subscribed,
      is_new: is_new
    });
  };
  var refreshTokenPromiseRef = useRef(null);
  var fetchUser = useCallback(function (accessToken) {
    onUserChange === null || onUserChange === void 0 ? void 0 : onUserChange(function (prev) {
      if (!prev) {
        getUser(accessToken).then(function (userData) {
          var _a;
          onUserChange === null || onUserChange === void 0 ? void 0 : onUserChange(extractUserInfo(userData));
          onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(authStatus.resolved);
          (_a = window === null || window === void 0 ? void 0 : window.pulse) === null || _a === void 0 ? void 0 : _a.call(window, 'state', {
            user_id: (userData === null || userData === void 0 ? void 0 : userData.id) || null
          });
        })["catch"](function (err) {
          if (!isAbortOrNavigationError(err)) {
            makeLogout();
          }
        });
      }
      return prev;
    });
  }, [makeLogout, onStatusChange, onUserChange]);
  var isVisible = usePageVisibility();
  var refreshTokenAndUser = useCallback(function () {
    if (disableRefreshCycle) return Promise.resolve();
    if (!refreshTokenPromiseRef.current) {
      refreshTokenPromiseRef.current = refreshToken().then(function (res) {
        var _a;
        startTokenLifecycle(res === null || res === void 0 ? void 0 : res.response);
        if ((_a = res === null || res === void 0 ? void 0 : res.response) === null || _a === void 0 ? void 0 : _a.access_token) {
          fetchUser(res.response.access_token);
        }
      })["catch"](function (error) {
        if (!isAbortOrNavigationError(error)) {
          makeLogout();
        }
      })["finally"](function () {
        refreshTokenPromiseRef.current = null;
      });
    }
    return refreshTokenPromiseRef.current;
  }, [disableRefreshCycle, fetchUser, makeLogout, startTokenLifecycle]);
  useIsomorphicLayoutEffect(function () {
    if (!isTokenFetchedRef.current && !(tokenDataRef === null || tokenDataRef === void 0 ? void 0 : tokenDataRef.current)) {
      isTokenFetchedRef.current = true;
      var authState = getStorage(authStateKey);
      if (authState === authStates.authorized) {
        refreshTokenAndUser();
      } else {
        onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(authStatus.failed);
      }
    } else if (!isTokenFetchedRef.current && (tokenDataRef === null || tokenDataRef === void 0 ? void 0 : tokenDataRef.current)) {
      isTokenFetchedRef.current = true;
      startTokenLifecycle(tokenDataRef === null || tokenDataRef === void 0 ? void 0 : tokenDataRef.current);
    }
  }, [refreshTokenAndUser]);
  useEffect(function () {
    isVisible && (tokenDataRef === null || tokenDataRef === void 0 ? void 0 : tokenDataRef.current) && refreshTokenAndUser();
  }, [isVisible]);
  useEffect(function () {
    onUserChange === null || onUserChange === void 0 ? void 0 : onUserChange(function (prevUser) {
      if (prevUser) {
        onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(function (prev) {
          return prev === authStatus.done ? authStatus.resolved : prev;
        });
      }
      return prevUser;
    });
  }, [onStatusChange, onUserChange]);
  return {
    makeLogout: makeLogout,
    refreshTokenAndUser: refreshTokenAndUser
  };
}

export { useBearerAuthentication as default };
//# sourceMappingURL=index.js.map
